import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package, PhoneCall, Truck } from "lucide-react";
import { trackingApi, type TrackingLive } from "@/features/delivery/api";
import { LiveMap } from "@/components/delivery/LiveMap";

const STATUS_LABEL: Record<string, string> = {
    placed: "Order placed",
    confirmed: "Confirmed",
    packing: "Packed",
    out_for_delivery: "Out for delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
};

export default function PublicTrack() {
    const { token = "" } = useParams<{ token: string }>();

    const { data, isLoading, isError } = useQuery<TrackingLive>({
        queryKey: ["public-track", token],
        queryFn: () => trackingApi.publicTrack(token),
        enabled: Boolean(token),
        refetchInterval: (q) => (q.state.data?.status === "delivered" ? false : 5000),
    });

    if (isLoading) {
        return (
            <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-muted-foreground">
                Loading tracking…
            </div>
        );
    }
    if (isError || !data) {
        return (
            <div className="grid min-h-screen place-items-center bg-slate-50 p-6 text-center">
                <div>
                    <h1 className="text-2xl font-extrabold">Tracking link expired</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Ask the customer for a fresh link.
                    </p>
                </div>
            </div>
        );
    }

    const riderLoc = data.rider?.current_location;
    const custGps = (data.customer_address as unknown as { gps?: { lat: number; lng: number } } | undefined)?.gps;

    return (
        <div data-testid="public-track-page" className="min-h-screen bg-slate-50">
            <header className="bg-ink px-4 py-4 text-white">
                <p className="text-xs uppercase tracking-widest text-white/60">TSGo tracking</p>
                <h1 className="text-lg font-bold">
                    {data.number} · {STATUS_LABEL[data.status] || data.status}
                </h1>
                {data.eta ? (
                    <p className="text-xs text-white/70">
                        ETA {new Date(data.eta).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                    </p>
                ) : null}
            </header>

            <main className="mx-auto max-w-2xl space-y-4 p-4">
                {riderLoc || custGps ? (
                    <LiveMap
                        rider={riderLoc ? { lat: riderLoc.lat, lng: riderLoc.lng } : null}
                        customer={custGps ? { lat: custGps.lat, lng: custGps.lng } : null}
                        path={(data.delivery as unknown as { path?: Array<{ lat: number; lng: number }> } | null)?.path?.map((p) => ({ lat: p.lat, lng: p.lng })) || []}
                        height={320}
                        testId="public-track-map"
                    />
                ) : (
                    <div className="rounded-2xl border border-dashed border-border/70 bg-white p-6 text-center text-sm text-muted-foreground">
                        Waiting for the rider to start moving…
                    </div>
                )}

                {data.rider ? (
                    <div className="rounded-2xl border border-border/70 bg-white p-4 shadow-soft" data-testid="public-track-rider">
                        <div className="flex items-center gap-3">
                            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand text-white">
                                <Truck className="h-5 w-5" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="font-bold">{data.rider.full_name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {data.rider.vehicle_type} · ★ {data.rider.rating.toFixed(1)}
                                </p>
                            </div>
                            <a
                                href={`tel:${data.rider.phone}`}
                                className="inline-flex items-center gap-2 rounded-full bg-brand px-3 py-2 text-xs font-bold text-white"
                            >
                                <PhoneCall className="h-3.5 w-3.5" /> Call
                            </a>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border/70 bg-white p-4 text-sm text-muted-foreground shadow-soft">
                        <Package className="mb-1 inline-block h-4 w-4 text-brand" /> Rider will be assigned shortly.
                    </div>
                )}

                <p className="text-center text-[11px] text-muted-foreground">
                    This link auto-refreshes every 5 seconds until delivery.
                </p>
            </main>
        </div>
    );
}
